import React from 'react';
import { Container, Typography, Grid, Card, CardContent, Box, Button, Paper, Avatar, Divider, Accordion, AccordionSummary, AccordionDetails } from '@mui/material';
import { Print, Support, Speed, Security, ExpandMore, Star, CheckCircle, People, LocalOffer, QuestionAnswer } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const Home = () => {
  const navigate = useNavigate();

  const features = [
    {
      icon: <Print sx={{ fontSize: 40 }} />,
      title: 'Expert Printer Support',
      description: 'Professional assistance for all your printer-related issues'
    },
    {
      icon: <Speed sx={{ fontSize: 40 }} />,
      title: 'Fast Response',
      description: 'Quick response times and efficient problem resolution'
    },
    {
      icon: <Security sx={{ fontSize: 40 }} />,
      title: 'Secure Service',
      description: 'Your data and printer security is our top priority'
    },
    {
      icon: <Support sx={{ fontSize: 40 }} />,
      title: '24/7 Support',
      description: 'Round-the-clock technical support for your convenience'
    }
  ];

  const testimonials = [
    {
      name: 'Sarah Johnson',
      role: 'Office Manager',
      company: 'Tech Solutions Inc.',
      avatar: 'https://randomuser.me/api/portraits/women/44.jpg',
      content: 'The printer support team was incredibly responsive and solved our network printing issues within minutes. Highly recommended!'
    },
    {
      name: 'Michael Chen',
      role: 'IT Director',
      company: 'Global Enterprises',
      avatar: 'https://randomuser.me/api/portraits/men/32.jpg',
      content: 'We\'ve been using their services for over a year now. Their expertise and professionalism have been invaluable to our operations.'
    },
    {
      name: 'Emily Rodriguez',
      role: 'Small Business Owner',
      company: 'Creative Designs',
      avatar: 'https://randomuser.me/api/portraits/women/68.jpg',
      content: 'As a small business owner, having reliable printer support is crucial. This team has never let me down, even during critical deadlines.'
    }
  ];

  const stats = [
    { icon: <People />, value: '5000+', label: 'Happy Clients' },
    { icon: <CheckCircle />, value: '98%', label: 'Satisfaction Rate' },
    { icon: <LocalOffer />, value: '24/7', label: 'Support Available' },
    { icon: <Star />, value: '4.9/5', label: 'Average Rating' }
  ];

  const faqs = [
    {
      question: 'What types of printers do you support?',
      answer: 'We support all major printer brands including HP, Canon, Epson, Brother, Xerox, and more. Our technicians are certified to work with both inkjet and laser printers, as well as specialized printing equipment.'
    },
    {
      question: 'How quickly can you respond to a printer emergency?',
      answer: 'We offer emergency support with response times as quick as 1 hour for critical issues. Our standard response time is within 4 hours for non-emergency situations.'
    },
    {
      question: 'Do you offer remote support?',
      answer: 'Yes, we provide remote support for many printer issues. Our technicians can securely connect to your system to diagnose and fix problems without needing to be physically present.'
    },
    {
      question: 'What is your pricing structure?',
      answer: 'We offer flexible pricing options including pay-as-you-go, monthly subscription plans, and annual contracts. Each plan can be customized to meet your specific needs and budget.'
    },
    {
      question: 'Do you provide printer maintenance services?',
      answer: 'Yes, we offer comprehensive maintenance services including regular check-ups, cleaning, calibration, and parts replacement to ensure your printers operate at peak performance.'
    }
  ];

  return (
    <Box>
      {/* Hero Section */}
      <Box
        sx={{
          position: 'relative',
          bgcolor: 'background.paper',
          pt: { xs: 6, sm: 8, md: 12 },
          pb: { xs: 8, sm: 10, md: 16 },
          overflow: 'hidden',
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={{ xs: 3, md: 4 }} alignItems="center">
            <Grid item xs={12} md={6} sx={{ order: { xs: 2, md: 1 } }}>
              <Typography 
                variant="h1" 
                component="h1" 
                gutterBottom
                sx={{
                  mb: 2,
                  fontSize: { xs: '2.5rem', sm: '3rem', md: '3.75rem' },
                  background: 'linear-gradient(45deg, #2563eb 30%, #7c3aed 90%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                Professional Printer Support
              </Typography>
              <Typography 
                variant="h5" 
                component="h2" 
                color="text.secondary" 
                sx={{ 
                  mb: 3, 
                  lineHeight: 1.6,
                  fontSize: { xs: '1.1rem', sm: '1.25rem', md: '1.5rem' }
                }}
              >
                Expert solutions for all your printing needs. Fast, reliable, and secure support when you need it most.
              </Typography>
              <Box sx={{ 
                display: 'flex', 
                flexDirection: { xs: 'column', sm: 'row' },
                gap: { xs: 2, sm: 2 },
                width: { xs: '100%', sm: 'auto' }
              }}>
                <Button
                  variant="contained"
                  size="large"
                  onClick={() => navigate('/contact')}
                  fullWidth={true}
                  sx={{ 
                    py: 1.5,
                    px: 4,
                    fontSize: '1.1rem'
                  }}
                >
                  Get Support Now
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  onClick={() => navigate('/services')}
                  fullWidth={true}
                  sx={{ 
                    py: 1.5,
                    px: 4,
                    fontSize: '1.1rem'
                  }}
                >
                  View Services
                </Button>
              </Box>
            </Grid>
            <Grid item xs={12} md={6} sx={{ order: { xs: 1, md: 2 }, mb: { xs: 4, md: 0 } }}>
              <Box
                sx={{
                  position: 'relative',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: -20,
                    right: -20,
                    width: '100%',
                    height: '100%',
                    background: 'linear-gradient(45deg, #2563eb 30%, #7c3aed 90%)',
                    opacity: 0.1,
                    borderRadius: 4,
                    zIndex: 0,
                  }
                }}
              >
                <Box
                  component="img"
                  src="https://lowestrate.in/wp-content/uploads/2024/07/61geKjTv9L._SL1500_.jpg"
                  alt="Professional Printer"
                  sx={{
                    width: '100%',
                    height: 'auto',
                    borderRadius: 4,
                    boxShadow: '0 20px 25px -5px rgb(0 0 0 / 0.1)',
                    position: 'relative',
                    zIndex: 1,
                    transition: 'transform 0.3s ease-in-out',
                    '&:hover': {
                      transform: 'scale(1.02)',
                    }
                  }}
                />
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Features Section */}
      <Box 
        sx={{ 
          position: 'relative',
          py: { xs: 6, sm: 8, md: 12 },
          bgcolor: 'background.default',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: 'radial-gradient(circle at 25px 25px, rgba(0, 0, 0, 0.05) 2%, transparent 0%), radial-gradient(circle at 75px 75px, rgba(0, 0, 0, 0.05) 2%, transparent 0%)',
            backgroundSize: '100px 100px',
            opacity: 0.5,
            zIndex: 0,
          }
        }}
      >
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Box sx={{ textAlign: 'center', mb: { xs: 4, md: 8 } }}>
            <Typography 
              variant="h2" 
              component="h2" 
              gutterBottom
              sx={{ 
                mb: 2,
                fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
                background: 'linear-gradient(45deg, #2563eb 30%, #7c3aed 90%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              Our Services
            </Typography>
            <Typography 
              variant="h5" 
              component="p" 
              color="text.secondary" 
              sx={{ 
                maxWidth: 800, 
                mx: 'auto',
                fontSize: { xs: '1rem', sm: '1.1rem', md: '1.25rem' }
              }}
            >
              We provide comprehensive printer support solutions to keep your business running smoothly
            </Typography>
          </Box>
          
          <Grid container spacing={{ xs: 2, sm: 3, md: 4 }} justifyContent="center">
            {features.map((feature, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Card 
                  sx={{ 
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    textAlign: 'center',
                    transition: 'all 0.3s ease-in-out',
                    position: 'relative',
                    overflow: 'hidden',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: '0 20px 25px -5px rgb(0 0 0 / 0.1)',
                      '& .icon-bg': {
                        transform: 'scale(1.1)',
                        opacity: 0.15,
                      }
                    }
                  }}
                >
                  <Box 
                    className="icon-bg"
                    sx={{ 
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      height: '120px',
                      background: 'linear-gradient(45deg, #2563eb 30%, #7c3aed 90%)',
                      opacity: 0.1,
                      transition: 'all 0.3s ease-in-out',
                      zIndex: 0,
                    }}
                  />
                  <CardContent sx={{ p: 4, position: 'relative', zIndex: 1 }}>
                    <Box 
                      sx={{ 
                        color: 'primary.main',
                        bgcolor: 'background.paper',
                        p: 2,
                        borderRadius: '50%',
                        mb: 3,
                        boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      {feature.icon}
                    </Box>
                    <Typography variant="h5" component="h3" gutterBottom fontWeight="600">
                      {feature.title}
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                      {feature.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
          
          <Box sx={{ mt: 8, textAlign: 'center' }}>
            <Button 
              variant="contained" 
              size="large"
              onClick={() => navigate('/services')}
              sx={{ 
                py: 1.5,
                px: 6,
                fontSize: '1.1rem'
              }}
            >
              View All Services
            </Button>
          </Box>
        </Container>
      </Box>

      {/* Statistics Section */}
      <Box sx={{ py: { xs: 6, sm: 8, md: 12 }, bgcolor: 'background.paper' }}>
        <Container maxWidth="lg">
          <Grid container spacing={{ xs: 2, sm: 3, md: 4 }} justifyContent="center">
            {stats.map((stat, index) => (
              <Grid item xs={6} md={3} key={index}>
                <Box 
                  sx={{ 
                    textAlign: 'center',
                    p: { xs: 2, sm: 3 },
                    borderRadius: 2,
                    transition: 'all 0.3s ease-in-out',
                    '&:hover': {
                      transform: 'translateY(-5px)',
                      boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)',
                    }
                  }}
                >
                  <Box 
                    sx={{ 
                      color: 'primary.main',
                      mb: 2,
                      display: 'flex',
                      justifyContent: 'center',
                      '& > svg': {
                        fontSize: { xs: 30, sm: 35, md: 40 }
                      }
                    }}
                  >
                    {stat.icon}
                  </Box>
                  <Typography 
                    variant="h3" 
                    component="div" 
                    sx={{ 
                      fontWeight: 700,
                      mb: 1,
                      fontSize: { xs: '1.75rem', sm: '2rem', md: '2.5rem' },
                      background: 'linear-gradient(45deg, #2563eb 30%, #7c3aed 90%)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                    }}
                  >
                    {stat.value}
                  </Typography>
                  <Typography 
                    variant="h6" 
                    color="text.secondary"
                    sx={{ fontSize: { xs: '0.875rem', sm: '1rem', md: '1.25rem' } }}
                  >
                    {stat.label}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Testimonials Section */}
      <Box 
        sx={{ 
          py: { xs: 6, sm: 8, md: 12 },
          bgcolor: 'background.default',
          position: 'relative',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '100%',
            background: 'linear-gradient(180deg, rgba(37, 99, 235, 0.05) 0%, rgba(124, 58, 237, 0.05) 100%)',
            zIndex: 0,
          }
        }}
      >
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Box sx={{ textAlign: 'center', mb: { xs: 4, md: 8 } }}>
            <Typography 
              variant="h2" 
              component="h2" 
              gutterBottom
              sx={{ 
                mb: 2,
                fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
                background: 'linear-gradient(45deg, #2563eb 30%, #7c3aed 90%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              What Our Clients Say
            </Typography>
            <Typography 
              variant="h5" 
              component="p" 
              color="text.secondary" 
              sx={{ 
                maxWidth: 800, 
                mx: 'auto',
                fontSize: { xs: '1rem', sm: '1.1rem', md: '1.25rem' }
              }}
            >
              Don't just take our word for it. Here's what our satisfied clients have to say about our services.
            </Typography>
          </Box>

          <Grid container spacing={{ xs: 2, sm: 3, md: 4 }} justifyContent="center">
            {testimonials.map((testimonial, index) => (
              <Grid item xs={12} md={4} key={index}>
                <Card 
                  sx={{ 
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    p: 3,
                    transition: 'all 0.3s ease-in-out',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: '0 20px 25px -5px rgb(0 0 0 / 0.1)',
                    }
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <Avatar 
                      src={testimonial.avatar} 
                      sx={{ 
                        width: 60, 
                        height: 60, 
                        mr: 2,
                        border: '3px solid',
                        borderColor: 'primary.main'
                      }} 
                    />
                    <Box>
                      <Typography variant="h6" component="div" fontWeight="600">
                        {testimonial.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {testimonial.role}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {testimonial.company}
                      </Typography>
                    </Box>
                  </Box>
                  <Typography variant="body1" color="text.secondary" sx={{ flexGrow: 1 }}>
                    "{testimonial.content}"
                  </Typography>
                  <Box sx={{ display: 'flex', mt: 3 }}>
                    {[1, 2, 3, 4, 5].map((star) => (
                      <Star key={star} sx={{ color: 'primary.main', fontSize: 20 }} />
                    ))}
                  </Box>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* FAQ Section */}
      <Box sx={{ py: { xs: 6, sm: 8, md: 12 }, bgcolor: 'background.paper' }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: { xs: 4, md: 8 } }}>
            <Typography 
              variant="h2" 
              component="h2" 
              gutterBottom
              sx={{ 
                mb: 2,
                fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
                background: 'linear-gradient(45deg, #2563eb 30%, #7c3aed 90%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              Frequently Asked Questions
            </Typography>
            <Typography 
              variant="h5" 
              component="p" 
              color="text.secondary" 
              sx={{ 
                maxWidth: 800, 
                mx: 'auto',
                fontSize: { xs: '1rem', sm: '1.1rem', md: '1.25rem' }
              }}
            >
              Find answers to common questions about our printer support services
            </Typography>
          </Box>

          <Box sx={{ maxWidth: 800, mx: 'auto' }}>
            {faqs.map((faq, index) => (
              <Accordion 
                key={index}
                sx={{ 
                  mb: 2,
                  borderRadius: 2,
                  overflow: 'hidden',
                  '&:before': {
                    display: 'none',
                  },
                  boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMore />}
                  sx={{ 
                    bgcolor: 'background.paper',
                    '&:hover': {
                      bgcolor: 'action.hover',
                    }
                  }}
                >
                  <Typography variant="h6" fontWeight="500">
                    {faq.question}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ bgcolor: 'background.default' }}>
                  <Typography variant="body1" color="text.secondary">
                    {faq.answer}
                  </Typography>
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>

          <Box sx={{ mt: 8, textAlign: 'center' }}>
            <Typography variant="h5" component="p" color="text.secondary" sx={{ mb: 3 }}>
              Still have questions? We're here to help!
            </Typography>
            <Button 
              variant="contained" 
              size="large"
              onClick={() => navigate('/contact')}
              sx={{ 
                py: 1.5,
                px: 6,
                fontSize: '1.1rem'
              }}
            >
              Contact Us
            </Button>
          </Box>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box 
        sx={{ 
          py: { xs: 6, sm: 8, md: 12 },
          bgcolor: 'primary.main',
          color: 'white',
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(45deg, rgba(37, 99, 235, 0.8) 0%, rgba(124, 58, 237, 0.8) 100%)',
            zIndex: 0,
          }
        }}
      >
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Grid container spacing={{ xs: 3, md: 4 }} alignItems="center">
            <Grid item xs={12} md={8}>
              <Typography 
                variant="h2" 
                component="h2" 
                gutterBottom 
                fontWeight="700"
                sx={{ fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' } }}
              >
                Ready to Get Started?
              </Typography>
              <Typography 
                variant="h5" 
                sx={{ 
                  mb: { xs: 3, md: 4 }, 
                  opacity: 0.9,
                  fontSize: { xs: '1.1rem', sm: '1.25rem', md: '1.5rem' }
                }}
              >
                Join thousands of satisfied clients who trust our printer support services.
              </Typography>
            </Grid>
            <Grid item xs={12} md={4} sx={{ textAlign: { xs: 'center', md: 'right' } }}>
              <Button
                variant="contained"
                size="large"
                onClick={() => navigate('/contact')}
                fullWidth={true}
                sx={{ 
                  py: 1.5,
                  px: 6,
                  fontSize: '1.1rem',
                  bgcolor: 'white',
                  color: 'primary.main',
                  '&:hover': {
                    bgcolor: 'grey.100',
                  }
                }}
              >
                Get Support Now
              </Button>
            </Grid>
          </Grid>
        </Container>
      </Box>
    </Box>
  );
};

export default Home; 